import { partition, reverse } from "@repo/utils/arrayUtils";
import { isEqual } from "@repo/utils/objectUtils";

//#region src/types.ts
let MutationApi;
(function(_MutationApi) {
	function invertMutationOp(op) {
		switch (op.type) {
			case "set": return "prevValue" in op ? {
				type: "set",
				collection: op.collection,
				value: op.prevValue
			} : {
				type: "remove",
				collection: op.collection,
				id: op.value.id
			};
			case "remove": return {
				type: "set",
				collection: op.collection,
				value: op.value
			};
			default: throw new Error("Unknown mutation op type");
		}
	}
	function getRollbackWrites(mutations) {
		return WriteOpsApi.merge(...reverse(mutations).map((mutation) => mutation.ops.map(invertMutationOp)).map(toWriteOps));
	}
	_MutationApi.getRollbackWrites = getRollbackWrites;
	function toWriteOps(ops) {
		const [setOps, removeOps] = partition(ops, (op) => op.type === "set");
		return {
			set: setOps.map((op) => ({
				key: [
					"record",
					op.collection,
					op.value.id
				],
				value: op.value
			})),
			remove: removeOps.map((op) => [
				"record",
				op.collection,
				op.id
			])
		};
	}
	_MutationApi.toWriteOps = toWriteOps;
	function opToDebugString(op) {
		switch (op.type) {
			case "set": return `set (${op.collection}) ${JSON.stringify(op.value, void 0, 2)}`;
			case "remove": return `remove (${op.collection}) ${op.id}`;
		}
	}
	function toString(mutation) {
		return `Mutation {\n${mutation.ops.map(opToDebugString).map((s) => `  ${s}`).join("\n")}\n}`;
	}
	_MutationApi.toString = toString;
	function intersectsQuery(mutation, query) {
		for (const op of mutation.ops) {
			const { collection } = query;
			if (op.collection !== collection) continue;
			return true;
		}
		return false;
	}
	_MutationApi.intersectsQuery = intersectsQuery;
	function intersectsScanWindow(mutation, scanWindow) {
		return scanWindow.some((query) => intersectsQuery(mutation, query));
	}
	_MutationApi.intersectsScanWindow = intersectsScanWindow;
})(MutationApi || (MutationApi = {}));
let WriteOpsApi;
(function(_WriteOpsApi) {
	function toString(writeOps) {
		return `WriteOps {\n${writeOps.set?.map((op) => `  set (${op.key[1]}) ${JSON.stringify(op.value, void 0, 2)}`).join("\n") ?? ""}\n${writeOps.remove?.map((op) => `  remove (${op})`).join("\n") ?? ""}}`;
	}
	_WriteOpsApi.toString = toString;
	function merge(...allWriteOps) {
		const target = {
			set: [],
			remove: []
		};
		for (const { set = [], remove = [] } of allWriteOps) {
			for (const { key, value } of set) {
				target.remove = target.remove?.filter((removedKey) => !isEqual(removedKey, key));
				target.set ??= [];
				target.set.push({
					key,
					value
				});
			}
			for (const key of remove) {
				target.set = target.set?.filter(({ key: keySet }) => !isEqual(keySet, key));
				target.remove ??= [];
				target.remove.push(key);
			}
		}
		return target;
	}
	_WriteOpsApi.merge = merge;
})(WriteOpsApi || (WriteOpsApi = {}));
let PatchApi;
(function(_PatchApi) {
	function toString(patch) {
		return `Patch {\n${patch.set?.map((op) => `  set ${op.collection}.${op.value.id} = ${JSON.stringify(op.value)}`).join("\n") ?? ""}\n${patch.remove?.map((op) => `  remove ${op.collection}.${op.id}`).join("\n") ?? ""}}`;
	}
	_PatchApi.toString = toString;
	function toWriteOps(patch) {
		const set = [];
		const remove = [];
		for (const s of patch.set ?? []) {
			const key = [
				"record",
				s.collection,
				s.value.id
			];
			set.push({
				key,
				value: s.value
			});
		}
		for (const r of patch.remove ?? []) remove.push([
			"record",
			r.collection,
			r.id
		]);
		return {
			set,
			remove
		};
	}
	_PatchApi.toWriteOps = toWriteOps;
})(PatchApi || (PatchApi = {}));

//#endregion
export { MutationApi, PatchApi, WriteOpsApi };